import { strapCaps, strapClock, strapDate, strapDesign, strapLineBoxes, type StrapDesign, type StrapDesignKey, type StrapTextBox } from '@airpost/shared';

/**
 * The station's lower-third designs on a canvas (ADR 083): the picture over the whole 16:9 frame, then the strap's
 * lines, the date and the clock in the design's boxes, scaled from 1920×1080. The pictures load once per page; until
 * one has loaded (or where it cannot), the straps are drawn as AirPost's schematic.
 */
export interface StrapArt {
  design: StrapDesign;
  img: CanvasImageSource;
}
/** When the strap is drawn, and whether its clock shows: a live preview shows it, a file for air does not. */
export interface StrapStamp {
  now: Date;
  clock: boolean;
}

export const STRAP_FONT = '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const pictures = new Map<StrapDesignKey, { img: HTMLImageElement | null; ready: Promise<HTMLImageElement | null> }>();

/** Load a design's picture (once); null where it cannot be loaded. */
export function loadStrapArt(key: StrapDesignKey): Promise<HTMLImageElement | null> {
  const known = pictures.get(key);
  if (known) return known.ready;
  if (typeof Image === 'undefined') return Promise.resolve(null);
  const entry: { img: HTMLImageElement | null; ready: Promise<HTMLImageElement | null> } = { img: null, ready: Promise.resolve(null) };
  entry.ready = new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      entry.img = img;
      resolve(img);
    };
    img.onerror = () => resolve(null);
    img.src = strapDesign(key).src;
  });
  pictures.set(key, entry);
  return entry.ready;
}

/** A design with its picture, when the picture has loaded; asking starts the load. */
export function strapArt(key: StrapDesignKey): StrapArt | null {
  void loadStrapArt(key);
  const img = pictures.get(key)?.img;
  return img ? { design: strapDesign(key), img } : null;
}

/** Whether a frame takes the designs: they are 16:9 pictures (a 9:16 or square edit keeps the schematic). */
export const frameTakesDesign = (w: number, h: number): boolean => h > 0 && Math.abs(w / h - 16 / 9) < 0.02;

/** Where a text of a box is written, in a frame of width `w`: its anchor x, its middle y, its width and its font. */
export function placeText(b: StrapTextBox, w: number): { x: number; y: number; maxW: number; font: string } {
  const k = w / 1920;
  const x = b.align === 'left' ? b.x : b.align === 'right' ? b.x + b.w : b.x + b.w / 2;
  return { x: x * k, y: (b.y + b.h / 2) * k, maxW: b.w * k, font: `${b.weight} ${Math.round(b.size * k * 10) / 10}px ${STRAP_FONT}` };
}

/** A box's place in a 16:9 preview on the page: in % of its width and height, its font in % of its width (container units). */
export function boxCss(b: StrapTextBox): { left: string; top: string; width: string; height: string; fontSize: string; fontWeight: number; color: string; justifyContent: string } {
  const pct = (v: number, of: number) => `${Math.round((v / of) * 100_000) / 1000}%`;
  return {
    left: pct(b.x, 1920),
    top: pct(b.y, 1080),
    width: pct(b.w, 1920),
    height: pct(b.h, 1080),
    fontSize: `${Math.round((b.size / 1920) * 100_000) / 1000}cqw`,
    fontWeight: b.weight,
    color: b.color,
    justifyContent: b.align === 'left' ? 'flex-start' : b.align === 'right' ? 'flex-end' : 'center',
  };
}

/** The texts a design shows for a strap: its lines (capitals where the box asks), the date, the clock when it shows. */
export function strapTexts(d: StrapDesign, lines: readonly string[], stamp: StrapStamp): Array<{ text: string; box: StrapTextBox }> {
  const out = strapLineBoxes(d, lines)
    .filter((l) => l.text)
    .map((l) => ({ text: l.box.upper ? strapCaps(l.text) : l.text, box: l.box }));
  if (d.date) out.push({ text: d.date.prefix + strapDate(stamp.now), box: d.date });
  if (d.clock && stamp.clock) out.push({ text: strapClock(stamp.now), box: d.clock });
  return out;
}

/** One strap in a design, over a frame of `w`×`h` (16:9). */
export function drawStrapArt(c: CanvasRenderingContext2D, art: StrapArt, lines: readonly string[], w: number, h: number, stamp: StrapStamp): void {
  c.save();
  c.drawImage(art.img, 0, 0, w, h);
  c.textBaseline = 'middle';
  for (const t of strapTexts(art.design, lines, stamp)) {
    const at = placeText(t.box, w);
    c.font = at.font;
    c.fillStyle = t.box.color;
    c.textAlign = t.box.align;
    c.fillText(t.text, at.x, at.y, at.maxW);
  }
  c.restore();
}
